import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import CartTableBudget from './CartTableBudget';
import { CartsContext } from './_CartsContext';
import CartListTopBar from './CartListTopBar';
import CartCheckout from './CartCheckout';
import SlideIconButton from '../../../../../reusable-components/basic-buttons/SlideIconButton';
import axiosInstance from '../../../../../utils/axios';
import LoadingAnimation from '../../../../../reusable-components/loader/LoadingAnimation';

CartList.propTypes = {
  canViewCart: PropTypes.bool,
  canCartCheck: PropTypes.bool,
  punchoutFailed: PropTypes.bool
}

export default function CartList({ canViewCart, canCartCheck, punchoutFailed }) {
  const { carts, facilityName, loading, setBulkDeleteItemsByIndex, checkoutCartItems, checkoutCartCategory, setCheckoutCart, facilityId } = useContext(CartsContext);

  const [showTableCheckbox, setShowTableCheckbox] = useState(false);

  const cartView = useMemo(() => {
    const cartsWithItems = carts.filter(cart => cart?.items?.length > 0);
    return cartsWithItems.map(cart => (
      <CartTableBudget
        key={cart.categoryId}
        cart={cart}
        singleCart={cartsWithItems.length === 1}
        facilityId={facilityId}
        setBulkDeleteItems={(items) => setBulkDeleteItemsByIndex(items, cart.categoryId)}
        canViewBudget={canViewCart}
        showTableCheckbox={showTableCheckbox}
      />
    ))
  }, [carts, showTableCheckbox]);

  const cartHasItems = carts?.some(cart => cart.items?.length > 0);

  return !loading ? <>
    {cartHasItems && <Box>
      <Box sx={{ mb: checkoutCartCategory ? -.5 : 1, display: "flex", alignItems: "center", pr: !checkoutCartItems ? "34px" : 0 }}>
        <Typography variant="subtitle1" sx={{ fontSize: "1rem", display: "flex", alignItems: "center" }} color="primary">
          {checkoutCartCategory && <SlideIconButton icon="mingcute:left-fill" text="Back to cart" onClick={() => setCheckoutCart(null)} />}
          {facilityName}
          {checkoutCartCategory && ` - ${checkoutCartCategory.category}`}
        </Typography>
        {!checkoutCartItems && <Box sx={{ ml: "auto" }}>
          <CartListTopBar canViewCart={canViewCart} canCartCheck={canCartCheck} showTableCheckbox={showTableCheckbox} setShowTableCheckbox={setShowTableCheckbox} />
        </Box>}
      </Box>
      {!checkoutCartItems && <>
        <Box sx={{
          maxHeight: "max(300px, calc(100vh - 220px))",
          overflowY: "scroll",
          overflowX: "hidden",
          "::-webkit-scrollbar": { width: "34px" },
          "::-webkit-scrollbar-thumb": {
            border: "14px solid rgba(0, 0, 0, 0)",
            backgroundClip: "padding-box",
            borderRadius: "9999px",
            backgroundColor: "#AAAAAA"
          }
        }}>
          {cartView}
        </Box>
      </>}
      {checkoutCartItems && <CartCheckout />}
    </Box>}
    {(!cartHasItems || punchoutFailed) && <CartListError noCarts={!cartHasItems} punchoutFailed={punchoutFailed} />}
  </> : <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: 'absolute', top: 0, left: 0, width: "100vw", height: "100vh" }} >
      <LoadingAnimation />
  </Box>;
}

CartListError.propTypes = {
  noCarts: PropTypes.bool,
  punchoutFailed: PropTypes.bool
}


function CartListError({ noCarts, punchoutFailed }) {
  const navigate = useNavigate();

  const [clearingBadCarts, setClearingBadCarts] = useState(false);
  const [open, setOpen] = useState(true);

  const goToOrders = () => {
    setClearingBadCarts(true);
    if (noCarts) {
      axiosInstance.post("ClearBadCarts")
      .catch(() => { })
      .finally(() => navigate("/dashboard/orders?filter=pending"))
    } else setOpen(false);
  }

  return <Dialog fullWidth maxWidth="xs" open={open} onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    goToOrders();
  }}>
    <DialogTitle sx={{ pb: 2 }}>Cart error </DialogTitle>
    <DialogContent sx={{ typography: 'body2' }}>
      {noCarts && <Alert severity="error" sx={{ mt: 1 }}>This cart is empty.</Alert>}
      {punchoutFailed && <Alert severity="error" sx={{ mt: 1 }}>Punchout failed.</Alert>}
    </DialogContent>
    <DialogActions>
      <LoadingButton loading={clearingBadCarts} variant="outlined" color="inherit" onClick={goToOrders}>
        {noCarts ? "Go to orders" : "Ok"}
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}
